// 이벤트 기준 데이터의 조건 해석. 원국은 출생 시 계산한 프로필을 사용한다.
import { GROUPS, GROUP_LABEL } from './rules.js';
import { natalProfile, preparationFor, choiceTimeCost } from './life.js';
import { PROFILE_METRICS, profileMetric } from './profile.js';
import { luckAt } from './luckPhase.js';
import { classifyTransition } from './luckTransition.js';
import { essenceOf } from './essence.js';
import { actOf } from './arc.js';
import { eventWhy } from './eventWhy.js';
import { prepareScene } from './scenes.js';

const TEN = [['비견', '겁재'], ['식신', '상관'], ['편재', '정재'], ['편관', '정관'], ['편인', '정인']];
export const CHOICE_KIND_LABEL = { act: '실행', decline: '하지 않기', alternative: '다른 방법' };
export const OUTCOME_LABEL = { success: '성공', failure: '실패', skipped: '하지 않음' };

// 옛 기록에서는 실제로 남아 있는 실패 여부만 사용한다. 알 수 없는 결과를 성공으로 채우지 않는다.
export function getEventHistory(s) {
  if (Array.isArray(s.eventHistory)) return s.eventHistory;
  return (s.log ?? []).filter((entry) => entry.kind === 'event' && entry.eventId
    && (entry.outcome || typeof entry.failed === 'boolean')).map((entry) => ({
    eventId: entry.eventId, age: entry.age, choiceIdx: entry.choiceIdx,
    choiceKind: entry.choiceKind ?? 'act',
    outcome: entry.outcome ?? (entry.failed ? 'failure' : 'success'),
    parentKey: entry.parentKey ?? null,
    contentVersion: entry.contentVersion ?? 0,
  })).sort((a, b) => a.age - b.age);
}

function natalMetric(s, condition) {
  const basis = s.saju?.statBasis;
  if (!basis) return null;
  if (condition.metric === 'support') return basis.daySupportIndex;
  if (condition.metric === 'usage') return basis.usageFactors?.[condition.field];
  if (condition.metric === 'rootCount') return s.saju.hour ? basis.dayRoots?.length : null;
  if (condition.metric === 'groupShare') {
    const index = GROUPS.indexOf(condition.field), force = basis.tenGodForces;
    if (index < 0 || !force) return null;
    const values = TEN.flat().map((key) => force[key]);
    if (!values.every(Number.isFinite)) return null;
    const total = values.reduce((sum, value) => sum + value, 0);
    return total > 0 ? TEN[index].reduce((sum, key) => sum + force[key], 0) / total : null;
  }
  return null;
}

function matchesCondition(s, condition) {
  if (condition.metric === 'combination') {
    const combinations = s.saju?.statBasis?.combinations;
    // 모르는 시주가 포함된 합을 확정 조건으로 쓰지 않는다.
    if (!s.saju?.hour || !Array.isArray(combinations)) return false;
    return combinations.some((entry) => entry.kind === condition.value && entry.status?.endsWith('적용'));
  }
  // 원국 프로필 조건(일간·격국·계절·조후·지원 구간): 문자열 값과 eq/ne 만 허용한다.
  if (PROFILE_METRICS.includes(condition.metric)) {
    const value = profileMetric(s.saju, condition.metric);
    if (value == null) return false;
    return condition.op === 'ne' ? value !== condition.value : condition.op === 'eq' && value === condition.value;
  }
  const actual = natalMetric(s, condition);
  if (!Number.isFinite(actual) || !Number.isFinite(condition.value)) return false;
  switch (condition.op) {
    case 'gte': return actual >= condition.value;
    case 'lte': return actual <= condition.value;
    case 'gt': return actual > condition.value;
    case 'lt': return actual < condition.value;
    case 'eq': return actual === condition.value;
    default: return false;
  }
}

function parentFor(s, event) {
  const after = event.rules?.after;
  if (!after) return null;
  const history = getEventHistory(s);
  return [...history].sort((a, b) => b.age - a.age).find((entry) => {
    const elapsed = s.age - entry.age;
    const key = `${entry.eventId}@${entry.age}`;
    return entry.eventId === after.eventId
      && (entry.contentVersion ?? 0) >= (after.minContentVersion ?? event.contentVersion ?? 0)
      && (!after.choiceKinds?.length || after.choiceKinds.includes(entry.choiceKind))
      && (!after.outcomes?.length || after.outcomes.includes(entry.outcome))
      && elapsed >= (after.minYears ?? 1) && elapsed <= (after.maxYears ?? 4)
      && !history.some((child) => child.eventId === event.id && child.parentKey === key);
  }) ?? null;
}

// 같은 원국(같은 객체)의 본질은 한 번만 계산한다. 진행 상태는 해마다 복제되므로 한 해 안의 후보 검사에서만 재사용된다.
const essenceCache = new WeakMap();
export function essenceFor(saju) {
  if (!saju || typeof saju !== 'object') return null;
  if (!essenceCache.has(saju)) essenceCache.set(saju, essenceOf(saju));
  return essenceCache.get(saju);
}

/** 조건을 만족한 후보만 반환한다. 추첨과 진행 상태 변경은 엔진에서 처리한다. */
export function prepareEvent(s, event, defaultCooldown = 5) {
  if (!event.choices?.length || !(event.baseWeight > 0)) return null;
  if (event.ageMin != null && s.age < event.ageMin) return null;
  if (event.ageMax != null && s.age > event.ageMax) return null;
  if (event.jobId && event.jobId !== s.job?.id) return null;
  const rule = event.rules ?? {}, job = rule.job;
  if (job && (!s.job || (job.ids?.length && !job.ids.includes(s.job.id))
      || (job.minLevel != null && s.job.level < job.minLevel)
      || (job.maxLevel != null && s.job.level > job.maxLevel))) return null;
  if (rule.once && (s.seen ?? []).includes(event.id)) return null;
  const lastAge = s.eventLastSeen?.[event.id];
  if (lastAge != null && s.age - lastAge < (event.cooldownYears ?? defaultCooldown)) return null;
  if (event.category === 'scene') return prepareScene(s, event);
  const conditions = rule.natal?.conditions ?? [];
  if (conditions.length) {
    const results = conditions.map((condition) => matchesCondition(s, condition));
    if (rule.natal.mode === 'any' ? !results.some(Boolean) : !results.every(Boolean)) return null;
  }
  if (rule.arc?.length) {
    // 서사 축(= 격국 이름)이 일치할 때만 후보가 된다. 원국을 해석할 수 없으면 나오지 않는다.
    const arcId = essenceFor(s.saju)?.arcId?.value;
    if (!arcId || !rule.arc.includes(arcId)) return null;
  }
  if (rule.act?.length) {
    const act = actOf(s.age);
    if (!act || !rule.act.includes(act)) return null;
  }
  if (rule.transition?.length) {
    // 대운 전환기이고 전환 유형이 일치할 때만 후보가 된다. 유형을 알 수 없으면(표 없음, 건록·월겁격) 나오지 않는다.
    const luck = luckAt(s.saju, s.age);
    const type = luck.inTransition ? classifyTransition(essenceFor(s.saju), luck) : null;
    if (!type || !rule.transition.includes(type)) return null;
  }
  // 서사 축·전환 조건 사건에는 화면에 보일 이유(왜 이 사건이 나왔는지)를 붙인다.
  let why;
  if (rule.arc?.length || rule.transition?.length) {
    const essence = essenceFor(s.saju), luck = luckAt(s.saju, s.age);
    why = eventWhy({ essence, age: s.age, luck, type: luck.inTransition ? classifyTransition(essence, luck) : null }, event) ?? undefined;
  }
  const parent = parentFor(s, event);
  if (rule.after && !parent) return null;
  const outcomeContext = parent && rule.context?.[parent.outcome];
  const authoredContext = typeof outcomeContext === 'string' ? outcomeContext : outcomeContext?.[parent?.choiceKind];
  const context = parent?.natalApproach ? `${parent.age}세의 선택: ${parent.choiceLabel}. ${parent.narrative ?? ''}` : authoredContext;
  const variantParent = parent && (parent.choiceKind !== 'act' || parent.outcome !== 'success');
  const title = !variantParent ? event.title : `${GROUP_LABEL[event.field]} 제안의 다음 선택을 정한다`;
  const description = variantParent ? '앞서 택한 방법과 현재 가능한 시간을 보고 이번 제안의 참여 범위를 다시 정합니다.' : event.description;
  return adaptLifeEvent(s, { ...event, ...(why ? { why } : {}), title,
    description: context ? `${context}\n\n${description}` : description,
    trigger: parent ? { eventId: parent.eventId, age: parent.age, choiceKind: parent.choiceKind,
      outcome: parent.outcome, key: `${parent.eventId}@${parent.age}`,
      deadline: parent.age + (rule.after.maxYears ?? 4) } : null,
    natalReason: rule.natal?.reason ?? null,
  });
}

const TRANSFER = {
  비겁: { field: '관성', label: '사람을 더 모으는 대신 역할과 약속을 문서로 정리한다' },
  식상: { field: '인성', label: '직접 작업하는 대신 진행 방법을 비교해 자료로 정리한다' },
  재성: { field: '비겁', label: '조건을 혼자 판단하는 대신 함께 검토할 사람과 의견을 나눈다' },
  관성: { field: '인성', label: '역할을 넓히는 대신 기준과 인수인계 자료를 정리한다' },
  인성: { field: '식상', label: '자료를 더 읽는 대신 개념 하나를 설명하는 결과물을 만든다' },
};
const ENTRY_SUPPORT = {
  비겁: { field: '인성', label: '모임 안내를 읽고 참여 전에 물어볼 질문을 정리한다' },
  식상: { field: '인성', label: '직접 완성하는 대신 작은 예제와 작업 방법부터 배운다' },
  재성: { field: '인성', label: '결정을 서두르지 않고 비용과 조건을 비교할 기준부터 배운다' },
  관성: { field: '비겁', label: '혼자 맡기 전에 함께할 사람과 역할 범위를 확인한다' },
  인성: { field: '비겁', label: '질문할 사람과 배울 범위를 상의하며 공부 방법을 찾는다' },
};

function adaptLifeEvent(s, event) {
  const profile = natalProfile(s, event.field), preparation = preparationFor(s, event.field);
  // 참여를 위해 포기하는 시간은 연간 시간 배분으로 표시한다. 다른 분야 능력을 현금처럼 지불하지 않는다.
  const choices = event.choices.map((choice) => {
    const effects = choiceEffects(choice), failureEffects = choiceEffects(choice, true);
    const retain = (values) => Object.fromEntries(Object.entries(values).filter(([field, value]) => value >= 0 || field === choice.targetField));
    return { ...choice, effects: retain(effects), failureEffects: retain(failureEffects), timeCost: choiceTimeCost(choice) };
  });
  let approachText = '', adapted = choices;
  if (event.category === 'common' && !event.rules?.after) {
    const smallStart = preparation.stage === 'prepare';
    const limited = (profile.kind === 'capacity' && preparation.stage !== 'ready') || preparation.stage === 'retry';
    if (smallStart || limited) {
      const scope = limited ? '범위를 줄여 재조정' : '입문 범위만 시작';
      approachText = limited ? '이번 제안은 맡을 분량을 줄여 다시 선택할 수 있습니다.' : '이번 제안은 작은 범위부터 시작할 수 있습니다.';
      adapted = choices.map((choice) => {
        if ((choice.kind ?? 'act') !== 'act') return choice;
        const positive = Object.fromEntries(Object.entries(choice.effects).map(([field, value]) => [field, value > 0 ? Math.min(smallStart ? 2 : 3, value) : value]));
        const failure = Object.fromEntries(Object.entries(choice.failureEffects).map(([field, value]) => [field, value < 0 ? Math.max(-1, value) : value]));
        return { ...choice, label: `${choice.label} · ${scope}`, effects: positive, failureEffects: failure,
          delta: positive[choice.targetField] ?? choice.delta, timeCost: 1, risk: Math.min(choice.risk ?? 0, .15),
          successText: `정한 작은 범위에서 진행했다. ${choice.successText ?? ''}`,
          failureText: `범위를 줄여 시도했지만 마무리하지 못했다. ${choice.failureText ?? ''}` };
      });
    }
    if (profile.kind === 'high') {
      const transfer = TRANSFER[event.field];
      adapted = adapted.map((choice) => choice.kind !== 'alternative' ? choice : { ...choice,
        label: transfer.label, targetField: transfer.field, delta: 2, effects: { [transfer.field]: 2 },
        failureEffects: { [transfer.field]: -1 }, risk: .1, timeCost: 1,
        successText: '이번 제안을 다른 영역의 준비로 전환해 정한 부분을 마쳤다.',
        failureText: '다른 방법으로 접근했지만 준비를 끝내지 못했다.' });
      approachText = `${approachText} 이 분야에만 활동을 더하는 대신 다른 영역으로 전환하는 선택지도 있습니다.`.trim();
    } else if (profile.kind === 'entry' && preparation.stage !== 'ready') {
      const support = ENTRY_SUPPORT[event.field];
      adapted = adapted.map((choice) => choice.kind !== 'alternative' ? choice : { ...choice,
        label: support.label, targetField: support.field, delta: 2, effects: { [support.field]: 2 },
        failureEffects: { [support.field]: -1 }, risk: .1, timeCost: 1,
        successText: '바로 맡기 전에 필요한 준비와 확인할 내용을 정리했다.',
        failureText: '준비 방법을 알아봤지만 이번에 필요한 내용을 정리하지 못했다.' });
      approachText = `${approachText} 안내나 도움을 통해 준비하는 방법도 고를 수 있습니다.`.trim();
    }
  }
  const preparationText = { prepare: '이 분야의 기록된 완료 사건이 없어 사전 준비부터 접근합니다.',
    ready: preparation.successes ? `이 분야에서 완료한 ${preparation.successes}회의 기록을 활용할 수 있습니다.`
      : `이 분야에 집중한 ${preparation.focusYears}년의 준비를 활용할 수 있습니다.`,
    retry: '이 분야의 마지막 시도는 완료하지 못했습니다. 이번에는 범위를 다시 정할 수 있습니다.' }[preparation.stage];
  const uncertain = profile.uncertain ? '시주 모름의 평균 출발 조건을 사용합니다.' : '';
  return { ...event, choices: adapted, approach: profile, preparation,
    description: [event.description, approachText].filter(Boolean).join('\n\n'),
    natalReason: [event.natalReason, preparationText, uncertain].filter(Boolean).join(' ') };
}

export function choiceEffects(choice, failed = false) {
  // 하지 않기는 보통 효과가 없다. 손실 사건에서 그대로 두는 선택(bearsCost)은 손실을 그대로 입는다.
  if (choice.kind === 'decline' && !choice.bearsCost) return {};
  const explicit = failed ? choice.failureEffects : choice.effects;
  if (explicit && typeof explicit === 'object' && !Array.isArray(explicit)) return explicit;
  const base = choice.effects && typeof choice.effects === 'object'
    ? choice.effects : { [choice.targetField]: choice.delta };
  return Object.fromEntries(Object.entries(base).filter(([field, value]) => GROUPS.includes(field) && Number.isFinite(value))
    .map(([field, value]) => [field, failed && value > 0 ? -Math.ceil(value / 2) : value]));
}

export function choiceEffectText(choice, failed = false) {
  const effects = Object.entries(choiceEffects(choice, failed)).filter(([, value]) => value !== 0);
  return effects.length ? effects.map(([field, value]) => `${GROUP_LABEL[field] ?? field} ${value > 0 ? '+' : ''}${value}`).join(' · ') : '능력치 변화 없음';
}
